import { useState, useRef } from "react";
import { Building, MapPin, Calendar, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion, useInView } from "framer-motion";
import AfricaMap from "./AfricaMap";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const { language } = useLanguage();

  const projects = [
    { id: 1, title: { fr: "Étude de Marché - Secteur AgTech", en: "Market Study - AgTech Sector" }, client: { fr: "Ministère de l'Agriculture", en: "Ministry of Agriculture" }, type: "market-study", year: "2024", country: { fr: "Sénégal", en: "Senegal" }, description: { fr: "Analyse complète du marché des technologies agricoles avec identification des opportunités de croissance.", en: "Comprehensive analysis of agricultural technology market with growth opportunity identification." }, tags: { fr: ["Étude de marché", "AgTech", "Innovation"], en: ["Market study", "AgTech", "Innovation"] } },
    { id: 2, title: { fr: "Audit Financier - Programme WASH", en: "Financial Audit - WASH Program" }, client: { fr: "ONG Internationale", en: "International NGO" }, type: "financial-analysis", year: "2024", country: { fr: "Mali", en: "Mali" }, description: { fr: "Audit financier et évaluation de la performance d'un programme d'accès à l'eau.", en: "Financial audit and performance evaluation of a water access program." }, tags: { fr: ["Audit", "Finance", "WASH"], en: ["Audit", "Finance", "WASH"] } },
    { id: 3, title: { fr: "Plan Stratégique - Transformation Digitale", en: "Strategic Plan - Digital Transformation" }, client: { fr: "Banque Régionale", en: "Regional Bank" }, type: "strategic-consulting", year: "2023", country: { fr: "Côte d'Ivoire", en: "Ivory Coast" }, description: { fr: "Accompagnement dans la définition et mise en œuvre d'un plan de transformation digitale sur 3 ans.", en: "Support in defining and implementing a 3-year digital transformation plan." }, tags: { fr: ["Transformation", "Digital", "Finance"], en: ["Transformation", "Digital", "Finance"] } },
    { id: 4, title: { fr: "Étude Sectorielle - E-commerce B2B", en: "Sector Study - B2B E-commerce" }, client: { fr: "Confidentiel", en: "Confidential" }, type: "market-study", year: "2023", country: { fr: "Maroc", en: "Morocco" }, description: { fr: "Analyse des tendances du e-commerce B2B et stratégies de positionnement.", en: "B2B e-commerce trends analysis and positioning strategies." }, tags: { fr: ["E-commerce", "B2B", "Maroc"], en: ["E-commerce", "B2B", "Morocco"] } },
    { id: 5, title: { fr: "Évaluation de Programme - Éducation", en: "Program Evaluation - Education" }, client: { fr: "Banque Mondiale", en: "World Bank" }, type: "financial-analysis", year: "2023", country: { fr: "Burkina Faso", en: "Burkina Faso" }, description: { fr: "Évaluation à mi-parcours d'un programme éducatif national.", en: "Mid-term evaluation of a national education program." }, tags: { fr: ["Éducation", "Évaluation", "Impact"], en: ["Education", "Evaluation", "Impact"] } },
    { id: 6, title: { fr: "Stratégie d'Expansion - Télécom", en: "Expansion Strategy - Telecom" }, client: { fr: "Opérateur Télécom", en: "Telecom Operator" }, type: "strategic-consulting", year: "2024", country: { fr: "Sénégal", en: "Senegal" }, description: { fr: "Stratégie d'expansion vers 3 nouveaux marchés africains.", en: "Expansion strategy towards 3 new African markets." }, tags: { fr: ["Télécom", "Expansion", "Stratégie"], en: ["Telecom", "Expansion", "Strategy"] } },
    { id: 7, title: { fr: "Étude de Faisabilité - Énergie Solaire", en: "Feasibility Study - Solar Energy" }, client: { fr: "Ministère de l'Énergie", en: "Ministry of Energy" }, type: "market-study", year: "2024", country: { fr: "Niger", en: "Niger" }, description: { fr: "Analyse de faisabilité pour un parc solaire de 50MW.", en: "Feasibility analysis for a 50MW solar park." }, tags: { fr: ["Énergie", "Solaire", "Faisabilité"], en: ["Energy", "Solar", "Feasibility"] } },
    { id: 8, title: { fr: "Diagnostic Organisationnel", en: "Organizational Diagnosis" }, client: { fr: "Institution Financière", en: "Financial Institution" }, type: "strategic-consulting", year: "2023", country: { fr: "Cameroun", en: "Cameroon" }, description: { fr: "Diagnostic complet et recommandations de restructuration organisationnelle.", en: "Complete diagnosis and organizational restructuring recommendations." }, tags: { fr: ["Organisation", "Diagnostic", "Finance"], en: ["Organization", "Diagnosis", "Finance"] } },
  ];

  const filters = [
    { id: "all", label: t(translations.projects.filters.all, language), count: projects.length },
    { id: "market-study", label: t(translations.projects.filters.marketStudy, language), count: projects.filter((p) => p.type === "market-study").length },
    { id: "financial-analysis", label: t(translations.projects.filters.financialAnalysis, language), count: projects.filter((p) => p.type === "financial-analysis").length },
    { id: "strategic-consulting", label: t(translations.projects.filters.strategicConsulting, language), count: projects.filter((p) => p.type === "strategic-consulting").length },
  ];

  const filteredProjects = projects.filter((p) => filter === "all" || p.type === filter);

  const getTypeColor = (type: string) => {
    switch (type) {
      case "market-study": return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800";
      case "financial-analysis": return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800";
      case "strategic-consulting": return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getTypeLabel = (type: string) => {
    const labels = translations.projects.typeLabels as Record<string, { fr: string; en: string }>;
    return labels[type] ? t(labels[type], language) : type;
  };

  return (
    <section id="projects" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">{t(translations.projects.badge, language)}</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">{t(translations.projects.title, language)}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(translations.projects.subtitle, language)}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          <motion.div
            className="lg:col-span-2 lg:sticky lg:top-32"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-[var(--shadow-card)]">
              <AfricaMap />
            </div>
          </motion.div>

          <div className="lg:col-span-3 space-y-6">
            <motion.div
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {filters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                    filter === f.id
                      ? 'bg-[hsl(var(--brand-navy))] text-white shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </motion.div>

            <div className="space-y-3">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.07, duration: 0.4 }}
                  className="group bg-card border border-border rounded-xl p-4 sm:p-5 hover:border-[hsl(var(--brand-red)/0.3)] hover:shadow-[var(--shadow-premium)] transition-all duration-400 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-base font-bold text-foreground group-hover:text-[hsl(var(--brand-red))] transition-colors truncate">
                          {t(project.title, language)}
                        </h3>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${getTypeColor(project.type)}`}>
                          {getTypeLabel(project.type)}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{t(project.description, language)}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                        <span className="flex items-center gap-1"><Building className="h-3 w-3" />{t(project.client, language)}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t(project.country, language)}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{project.year}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {project.tags[language].map((tag, i) => (
                          <span key={i} className="text-[10px] px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-[hsl(var(--brand-red))] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
              className="text-center pt-4"
            >
              <p className="text-sm text-muted-foreground">
                {t(translations.projects.showing, language)} <span className="font-semibold text-foreground">{filteredProjects.length}</span> {t(translations.projects.projectWord, language)}{filteredProjects.length > 1 ? "s" : ""} {t(translations.projects.on, language)} <span className="font-semibold text-foreground">{projects.length}</span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
